import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useEffect, useMemo } from "react";

import { useDashboardsQuery, useDefaultDashboardQuery } from "@/features/dashboard";
import { usePageHeader } from "@/shared/store/page-header";

export const Route = createFileRoute("/_authenticated/dashboard/")({
  component: DashboardIndexPage,
});

function DashboardIndexPage() {
  const navigate = useNavigate();
  const { data: defaultDashboard, isLoading: isLoadingDefault } = useDefaultDashboardQuery();
  const { data: dashboardsData, isLoading: isLoadingList } = useDashboardsQuery();
  const { setTitle, reset } = usePageHeader();

  useEffect(() => {
    setTitle("Dashboards");
    return () => reset();
  }, [setTitle, reset]);

  const isLoading = isLoadingDefault || isLoadingList;
  const dashboards = useMemo(() => dashboardsData?.dashboards ?? [], [dashboardsData?.dashboards]);

  // Redirect to default dashboard if one exists
  useEffect(() => {
    if (!isLoading && defaultDashboard) {
      void navigate({ to: `/dashboard/${defaultDashboard.slug}`, replace: true });
    }
  }, [isLoading, defaultDashboard, navigate]);

  // Redirect to first dashboard if no default but dashboards exist
  useEffect(() => {
    if (!isLoading && !defaultDashboard && dashboards.length > 0) {
      const firstDashboard = dashboards[0];
      if (firstDashboard) {
        void navigate({ to: `/dashboard/${firstDashboard.slug}`, replace: true });
      }
    }
  }, [isLoading, defaultDashboard, dashboards, navigate]);

  // Show loading while checking for default dashboard
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading dashboards...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no dashboards exist
  if (dashboards.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-semibold text-foreground">Welcome to Dashboards</h1>
          <p className="mt-2 text-muted-foreground">
            Get started by creating your first dashboard. Dashboards help you visualize and track
            your important metrics.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Click the <Plus className="inline-block size-4" /> button in the sidebar to create a new
            dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Fallback - should not reach here if redirects work
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <p className="text-muted-foreground">Redirecting...</p>
      </div>
    </div>
  );
}
